import { Box, Stack } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ScoreType } from '@/domain/impact/impact.entity';
import { ProjectType } from '@/domain/pdf-reports/pdf-reports.type';
import { deleteProjectTour } from '@/domain/project-tour/actions/delete-project-tour.action';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { selectCurrentProjectTour } from '@/domain/project-tour/project-tour.selectors';
import {
  scrollQuizPanelToId,
  scrollQuizPanelToTop,
} from '@/ui/components/quiz-panel/QuizPanel.utils';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { DEFAULT_LAYOUT_MARGIN } from '@/ui/pages/app/components/layouts/DefaultLayout';
import { ModalReportExporter } from '@/ui/pages/app/components/project-page/ModalReportExporter';
import { PageProjectHeader } from '@/ui/pages/app/components/project-page/PageProjectHeader';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { ProjectScoresDetailsItemParams } from '../../../components/project-page/ProjectScoresDetails/ProjectScoresDetails.types';
import { HeaderButtons } from './components/HeaderButtons';
import { ProjectTourMainContent } from './components/ProjectTourMainContent';
import { SidePanelQuiz } from './components/ProjectTourSidePanelQuiz';

export function ProjectTour() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [selectedScoreType, setSelectedScoreType] = useState<ScoreType>('co2');
  const [selectedDetailsValueId, setSelectedDetailsValueId] =
    useState<string>('');

  const [isQuizLayoutDisplayed, setIsQuizLayoutDisplayed] = useState(false); // TODO refacto (et dans festival aussi si possible)
  const [reportExporterModalOpen, setReportExporterModalOpen] = useState(false);

  const project = useAppSelector(selectCurrentProjectTour);

  const onClickBack = () => {
    navigate('/app/projects/tour');
  };

  const onClickEdit = () => {
    navigate(`/app/projects/tour/${project?.id}/edit`);
  };

  const onClickDelete = async () => {
    try {
      await dispatch(deleteProjectTour(project?.id || -1)).unwrap();
      navigate('/app/projects/tour');
    } catch (error) {
      console.error(error);
    }
  };

  const onClickExportReport = () => {
    setReportExporterModalOpen(true);
  };

  const onClickOpenOnTourSequence = (sequenceId: number) => {
    navigate(`/app/projects/tour/${project?.id}/ontour/${sequenceId}`);
  };

  const onClickDetailsItem = (params: ProjectScoresDetailsItemParams) => {
    const { scoreType, id, section } = params;
    setIsQuizLayoutDisplayed(true);
    setSelectedScoreType(scoreType);
    if (scoreType === 'co2') {
      setSelectedDetailsValueId(section || '');
      if (id) {
        scrollQuizPanelToId(`#${id}-form`);
      } else {
        scrollQuizPanelToTop();
      }
    }
    if (scoreType === 'ess' || scoreType === 'env') {
      setSelectedDetailsValueId(id);
      if (section) {
        scrollQuizPanelToId(`#${section.toLowerCase()}-form`);
      } else {
        scrollQuizPanelToTop();
      }
    }
  };

  if (!project) {
    return null;
  }

  return (
    <>
      <ModalReportExporter
        open={reportExporterModalOpen}
        onClose={() => setReportExporterModalOpen(false)}
        project={project}
        projectType={ProjectType.TOUR}
      />
      <Stack direction={'row'}>
        <Stack sx={{ minHeight: '100vh', width: 'calc(100% - 21rem)' }}>
          <Box sx={{ margin: DEFAULT_LAYOUT_MARGIN }}>
            <PageProjectHeader
              project={project}
              backButtonText={translate('ProjectsTour.my-tours')}
              onClickBack={onClickBack}
              rightComponent={
                <HeaderButtons
                  onClickEdit={onClickEdit}
                  onClickDelete={onClickDelete}
                  onClickExportReport={onClickExportReport}
                />
              }
            />
            <ProjectTourMainContent
              project={project}
              onClickDetailsItem={onClickDetailsItem}
              setSelectedScoreType={setSelectedScoreType}
              onClickOpenOnTourSequence={onClickOpenOnTourSequence}
            />
          </Box>
        </Stack>
        {!isQuizLayoutDisplayed && <SidePanelEmpty />}
        {isQuizLayoutDisplayed && (
          <SidePanelQuiz
            project={project}
            selectedScoreType={selectedScoreType}
            selectedSubType={selectedDetailsValueId}
            isQuizLayoutDisplayed={isQuizLayoutDisplayed}
            setIsQuizLayoutDisplayed={setIsQuizLayoutDisplayed}
          />
        )}
      </Stack>
    </>
  );
}

function SidePanelEmpty() {
  return (
    <Box
      sx={{
        width: '21rem',
        borderLeft: `1px solid ${ColorsRaw.anthracite.Anthracite_4}`,
      }}
    ></Box>
  );
}
